import React, { useState } from 'react';
import styled from 'styled-components';
import logo from '../../Assets/Logos/b_logo_white.svg';
import slogan from '../../Assets/Logos/baemingo_logo_white.svg';
import GetBack from '~Scenes/SelfServiceKiosks/Components/GetBack';
import { useStorage } from '~Components/Storage';
import { useAdyen, useI18n, useLogger, useSelector } from '~Hooks';
import NumberInput from '~ComponentsImproved/Numpad';
import { STAFF_PERMISSIONS } from '../../../__queries__';
import RefundList from './RefundList';
import { ImpulseSpinner } from '~Components/ImpulseSpinner';

const Wrapper = styled.div`
  width: 100vw;
  background: #000;
  height: calc(100vh - 60px);
  padding-top: 160px;
  display: flex;
  align-items: center;
  flex-direction: column;
`;

const Button = styled.div<{
  disabled?: boolean;
}>`
  color: #333;
  background: #fff;
  position: absolute;
  bottom: 30px;
  left: 30px;
  right: 100px;
  padding: 25px;
  font-size: 25px;
  border-radius: 8px;
  text-align: center;
  font-weight: bold;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.2);
`;

const MenuButton = styled.div<{
  disabled?: boolean;
}>`
  color: #333;
  background: #fff;
  position: absolute;
  bottom: 30px;
  right: 30px;
  width: 8px;
  padding: 25px;
  padding-top: 15px;
  padding-bottom: 35px;
  font-size: 35px;
  border-radius: 8px;
  text-align: center;
  line-height: 12.1px;
  font-weight: bold;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.2);
`;

const GoBack = styled.div`
  display: flex;
  align-items: center;

  position: absolute;
  top: 30px;
  left: 30px;
`;

const Logo = styled.div`
  height: 136px;
  mask-image: url(${logo});
  width: 138px;
  mask-size: contain;
  margin: 20px auto;
  background-color: #fff;
  mask-repeat: no-repeat;
  mask-position: center;
`;

const Slogan = styled.div`
  height: 76px;
  margin: 0 auto;
  mask-image: url(${slogan});
  width: 188px;
  mask-size: contain;
  background-color: #fff;
  mask-position: center;
  mask-repeat: no-repeat;
`;

const ErrorBox = styled.div`
  width: 300px;
  padding: 20px;
  text-align: center;
  color: #fff;
  font-weight: bold;
  border-top: 7px solid #ae2929ff;
`;

const PaymentStarting = styled.div`
  position: relative;
`;

const Standalone = () => {
  const logger = useLogger('standalone');

  const [showInput, setShowInput] = useState(false);
  const [showStaffInput, setShowStaffInput] = useState(false);

  const adyen = useAdyen();
  const { i18n } = useI18n();

  const [error, setError] = useState('');

  const [storage] = useStorage();
  const adyenTerminal = storage.adyenTerminals[0];

  const allStaff = useSelector((state) => state.companyStaff);
  const staffThatCanRefund =
    allStaff?.staffs.filter((staff) =>
      staff.accessLevel.permissions.includes(STAFF_PERMISSIONS.REFUND),
    ) || [];
  const validCodes = staffThatCanRefund.flatMap((staff) => staff.code);
  const [showRefundUI, setShowRefundUI] = useState(false);

  const [paymenStarting, setPaymentStarting] = useState(false);

  function handlePaymentStart(value: string) {
    logger.info(`Flow started (${value})`);

    const serviceId = Math.round(Math.random() * 1000000000).toString();

    logger.info('Payment starting. Amount: ' + value, {
      paymentId: serviceId,
    });

    adyen
      .pay(adyenTerminal!.POIID, {
        amount: Math.round(parseFloat(value) * 100),
        transactionId: Math.random().toString(),
        saleId: 'device',
        serviceId,
      })
      .then((result) => {
        logger.info('Payment completed', {
          paymentId: serviceId,
          result,
        });
        setPaymentStarting(false);
      })
      .catch((reason) => {
        logger.warn('Payment failed: ' + reason, {
          paymentId: serviceId,
        });
        setPaymentStarting(false);

        setError(reason.replace('POI', 'terminal'));
      });

    setShowInput(false);
    setPaymentStarting(true);
  }

  return (
    <Wrapper>
      <GetBack />
      {!error && <Logo />}
      {!error && <Slogan />}
      {showInput && (
        <NumberInput
          onClose={() => {
            setShowInput(false);
          }}
          onSubmit={handlePaymentStart}
          title="Enter Amount"
          submitText="Submit"
          decimal
        />
      )}

      {showStaffInput && (
        <NumberInput
          submitText="Authorize"
          title="Enter staff code"
          onClose={() => {
            setShowStaffInput(false);
          }}
          onSubmit={(value) => {
            const valid = validCodes.includes(value);

            if (valid) {
              setShowRefundUI(true);
            } else {
              setError('Invalid code');
            }

            setShowStaffInput(false);
          }}
        />
      )}

      {error && <ErrorBox>{error}</ErrorBox>}

      {paymenStarting ? (
        <PaymentStarting>
          <ImpulseSpinner size={50} />
        </PaymentStarting>
      ) : (
        <>
          {!error && (
            <MenuButton
              onTouchStart={() => {
                if (!allStaff || allStaff.staffs.length === 0) {
                  logger.info('No staff added, showing refund UI');
                  setShowRefundUI(true);
                } else {
                  setShowStaffInput(true);
                }
              }}>
              . . .
            </MenuButton>
          )}

          {error ? (
            <Button onTouchStart={() => setError('')}>OK</Button>
          ) : (
            <Button onTouchStart={() => setShowInput(true)}>{i18n('Express.Cart.Pay')}</Button>
          )}
        </>
      )}

      {showRefundUI && <RefundList onClose={() => setShowRefundUI(false)} />}
    </Wrapper>
  );
};

export default Standalone;
